import moment from 'moment'
import CurrencyFormatter from 'currency-formatter'
import { Option } from '../types'

/**
 * Partner excess-history codes accepted by excessHistoryDetails.type.
 * `code` is the StrataTech duty-of-disclosure option code.
 * `apiType` is ExcessHistoryType.name().
 */
export const EXCESS_HISTORY_TYPES: { apiType: string, code: string, description: string }[] = [
    { apiType: 'ACC_DAMAGE', code: 'ACCIDENTAL_DAMAGE', description: 'Accidental Damage' },
    { apiType: 'BURST_PIPE_EXPL_COSTS', code: 'EXPLORATORY_COSTS', description: 'Burst Pipe Exploratory Costs' },
    { apiType: 'EARTHQUAKE', code: 'EARTHQUAKE', description: 'Earthquake' },
    { apiType: 'FIRE', code: 'FIRE', description: 'Fire' },
    { apiType: 'FLOOD', code: 'FLOOD', description: 'Flood' },
    { apiType: 'FUSION', code: 'FUSION', description: 'Fusion' },
    { apiType: 'GLASS', code: 'GLASS', description: 'Glass' },
    { apiType: 'IMPACT_DAMAGE', code: 'IMPACT', description: 'Impact Damage' },
    { apiType: 'MALICIOUS_DAMAGE_VANDALISM', code: 'MALICIOUS_DAMAGE', description: 'Malicious Damage/Vandalism' },
    { apiType: 'STORM', code: 'STORM', description: 'Storm' },
    { apiType: 'THEFT', code: 'THEFT', description: 'Theft' },
    { apiType: 'WATER_DAMAGE', code: 'WATER', description: 'Water Damage' },
    { apiType: 'CYBER', code: 'CYBER', description: 'Cyber' },
    { apiType: 'FIDELITY_GUARANTEE', code: 'FIDELITY_GUARANTEE', description: 'Fidelity Guarantee' },
    { apiType: 'GOVT_AUDIT_COSTS', code: 'GOVERNMENT_AUDIT_COSTS', description: 'Government Audit Costs' },
    { apiType: 'LEGAL_DEFENCE_EXPENSES', code: 'LEGAL_DEFENCE_EXPENSES', description: 'Legal Defence Expenses' },
    { apiType: 'LEGAL_OHS', code: 'LEGAL_OHS', description: 'Legal OHS' },
    { apiType: 'PUB_LIAB', code: 'PUBLIC_LIABILITY', description: 'Public Liability' },
    { apiType: 'PUB_LIAB_MAT_DMG', code: 'PUBLIC_LIABILITY_MATERIAL_DAMAGE', description: 'Public Liability / Material Damage' },
    { apiType: 'MACH_BREAKDOWN', code: 'MACHINERY_BREAKDOWN', description: 'Machinery Breakdown' },
    { apiType: 'OFFICE_BEARERS_LIAB', code: 'OFFICE_BEARERS_LIABILITY', description: 'Office Bearers Liability' },
    { apiType: 'VOLUNTARY_WORKERS', code: 'VOLUNTARY_WORKERS', description: 'Voluntary Workers' },
    { apiType: 'OTHER', code: 'OTHER', description: 'Other' },
]

export const OTHER_EXCESS_CODE = 'OTHER'

export const CLAIM_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
]

export const CLAIM_STATUSES = ['Closed', 'Open'] as const

export const CLAIM_TOTAL_WINDOW_MONTHS = 36

export const CLAIM_YEAR_WINDOW_MONTHS = 37

export const CLAIM_ROWS_REQUIRED = 'Please add at least one claim, or select "No" if you have not had any claims in the past three (3) years.'
export const EXCESS_ROWS_REQUIRED = 'Please add at least one imposed excess, or select "No" if your current policy has not had any additional excesses imposed.'
export const CLAIM_ENTRY_REQUIRED = 'Please select a year, month and type of loss, and enter an amount before adding.'
export const EXCESS_ENTRY_REQUIRED = 'Please select an excess type and enter an amount before adding. If you selected "Other", please also enter the excess name.'
export const AMOUNT_INVALID = 'Please enter a valid amount.'
export const EXCESS_DUPLICATE = 'You have already added this excess type. Please choose a different type, or remove the existing entry before adding it again.'

export const YES_NO_OPTIONS: Option<boolean>[] = [
    { type: 'YES_NO', value: true, description: 'Yes' },
    { type: 'YES_NO', value: false, description: 'No' },
]

export type ExcessHistoryDetailApi = {
    type: string,
    otherType?: string,
    amount: number,
}

export type ClaimHistoryDetailApi = {
    lossDate: string,
    claimType: string,
    claimStatus: string,
    claimDescription?: string,
    paidAmount: number,
    outstandingAmount: number,
}

export function claimExcessTypeOptions(productOptions: Option<string>[] | undefined): Option<string>[] {
    const knownCodes = new Set(EXCESS_HISTORY_TYPES.map(type => type.code))
    const mapped = (productOptions || [])
        .filter(option => knownCodes.has(option.value))
        .map(option => ({
            ...option,
            description: option.description.replace(/<\/?wbr>/g, ''),
        }))

    if (mapped.length > 0) return mapped

    return EXCESS_HISTORY_TYPES.map(type => ({
        type: 'DOD_CLAIM_EXCESS_TYPE',
        value: type.code,
        description: type.description,
    }))
}

export function excessTypeByCode(code: string) {
    return EXCESS_HISTORY_TYPES.find(type => type.code === code)
}

export function excessTypeByApiType(apiType: string) {
    return EXCESS_HISTORY_TYPES.find(type => type.apiType === apiType)
}

export function excessTypeLabel(detail: ExcessHistoryDetailApi, options: Option<string>[]): string {
    if (detail.type === 'OTHER' && detail.otherType) return detail.otherType
    const match = excessTypeByApiType(detail.type)
    if (!match) return detail.type
    const option = options.find(item => item.value === match.code)
    return option ? option.description : match.description
}

export function hasExcessType(rows: ExcessHistoryDetailApi[] | undefined, apiType: string) {
    return (rows || []).some(row => row.type === apiType)
}

export function parseAmount(value: unknown): number {
    if (value === '' || value == null) return NaN
    if (typeof value === 'number') return value
    return parseFloat(String(value).replace(/[^0-9.-]/g, ''))
}

export function toExcessHistoryDetail(code: string, otherType: string, amount: number): ExcessHistoryDetailApi | null {
    const match = excessTypeByCode(code)
    if (!match || !(amount > 0)) return null
    const detail: ExcessHistoryDetailApi = {
        type: match.apiType,
        amount: Math.round(amount),
    }
    if (match.code === OTHER_EXCESS_CODE) {
        const other = (otherType || '').trim()
        if (!other) return null
        detail.otherType = other
    }
    return detail
}

export function claimYearOptions(startDate: string | undefined): Option<string>[] {
    const inception = moment(startDate, 'DD/MM/YYYY', true)
    const inceptionMoment = inception.isValid() ? inception : moment()
    const earliestYear = inceptionMoment.clone().subtract(CLAIM_YEAR_WINDOW_MONTHS, 'months').year()
    const years: Option<string>[] = []
    for (let year = inceptionMoment.year(); year >= earliestYear; year--) {
        years.push({
            type: 'CLAIM_YEAR',
            value: String(year),
            description: String(year),
        })
    }
    return years
}

export function claimMonthOptions(): Option<string>[] {
    return CLAIM_MONTHS.map(month => ({
        type: 'CLAIM_MONTH',
        value: month,
        description: month,
    }))
}

export function claimStatusOptions(): Option<string>[] {
    return CLAIM_STATUSES.map(status => ({
        type: 'CLAIM_STATUS',
        value: status,
        description: status,
    }))
}

export function toClaimHistoryDetail(input: {
    year: string,
    month: string,
    status: string,
    code: string,
    details: string,
    amount: number,
}): ClaimHistoryDetailApi | null {
    const monthIndex = CLAIM_MONTHS.indexOf(input.month)
    if (!input.year || monthIndex < 0 || !input.code || !(input.amount > 0)) return null
    if (input.status !== 'Open' && input.status !== 'Closed') return null

    const monthNumber = monthIndex + 1
    const monthText = monthNumber < 10 ? `0${monthNumber}` : String(monthNumber)
    const amount = Math.round(input.amount)
    const description = (input.details || '').replace(/[0-9]/g, '').trim()
    const detail: ClaimHistoryDetailApi = {
        lossDate: `01/${monthText}/${input.year}`,
        claimType: input.code,
        claimStatus: input.status,
        paidAmount: input.status === 'Closed' ? amount : 0,
        outstandingAmount: input.status === 'Open' ? amount : 0,
    }
    if (description) detail.claimDescription = description
    return detail
}

export function claimAmount(detail: ClaimHistoryDetailApi) {
    return Number(detail.paidAmount || 0) + Number(detail.outstandingAmount || 0)
}

export function claimYear(detail: ClaimHistoryDetailApi) {
    const parts = (detail.lossDate || '').split('/')
    return parts[2] || ''
}

export function claimMonth(detail: ClaimHistoryDetailApi) {
    const parts = (detail.lossDate || '').split('/')
    const monthNumber = parseInt(parts[1], 10)
    return CLAIM_MONTHS[monthNumber - 1] || ''
}

export function claimCountsTowardTotals(lossDate: string, today: Date = new Date()) {
    const loss = moment(lossDate, 'DD/MM/YYYY', true)
    if (!loss.isValid()) return false
    const end = moment(today).startOf('day')
    const start = end.clone().subtract(CLAIM_TOTAL_WINDOW_MONTHS, 'months')
    return loss.isSameOrAfter(start, 'day') && loss.isSameOrBefore(end, 'day')
}

export function claimTotals(rows: ClaimHistoryDetailApi[] | undefined, today: Date = new Date()) {
    const counted = (rows || []).filter(row => claimCountsTowardTotals(row.lossDate, today))
    return {
        numberOfClaims: counted.length,
        valueOfClaims: counted.reduce((sum, row) => sum + claimAmount(row), 0),
    }
}

export function formatDollar(amount: number) {
    return '$' + CurrencyFormatter.format(amount || 0, {
        thousand: ',',
        precision: 0,
    })
}

export function claimTypeLabel(code: string, options: Option<string>[]) {
    const option = options.find(item => item.value === code)
    if (option) return option.description
    const match = excessTypeByCode(code)
    return match ? match.description : code
}

export function resolveExcessHistory(coverLimited: boolean | undefined, excessHistory: boolean | undefined) {
    if (excessHistory === true || excessHistory === false) return excessHistory
    if (coverLimited === false) return false
    return undefined
}

type QuotingFields = {
    coverLimited?: boolean,
    excessHistory?: boolean,
    excessHistoryDetails?: ExcessHistoryDetailApi[],
    __hasClaims__?: boolean,
    claimsHistoryDetailApi?: ClaimHistoryDetailApi[],
}

export function simplifiedQuotingRequestFields(fields: QuotingFields) {
    const result: Record<string, unknown> = {}
    const excessHistory = resolveExcessHistory(fields.coverLimited, fields.excessHistory)
    if (fields.excessHistory === true && fields.excessHistoryDetails && fields.excessHistoryDetails.length > 0) {
        result.excessHistory = true
        result.excessHistoryDetails = fields.excessHistoryDetails
    } else if (excessHistory === false) {
        result.excessHistory = false
    }

    if (fields.__hasClaims__ === true && fields.claimsHistoryDetailApi && fields.claimsHistoryDetailApi.length > 0) {
        const totals = claimTotals(fields.claimsHistoryDetailApi)
        result.numberOfClaims = totals.numberOfClaims
        result.valueOfClaims = totals.valueOfClaims
        result.claimsHistoryDetailApi = fields.claimsHistoryDetailApi
    }

    if (fields.__hasClaims__ === false) {
        result.numberOfClaims = 0
        result.valueOfClaims = undefined
        result.claimsHistoryDetailApi = []
    }

    return result
}
